/**
 * FeaturedCollections Component
 * Category cards with product images
 */

import { motion } from 'motion/react';
import {
  IconArrowRight,
  IconMicrosoft,
  IconBluetooth,
  IconNoiseCancelling,
  IconReturn,
} from '../ui/Icons';

const collections = [
  {
    title: 'Teams & Zoom',
    description: 'Certified for video conferencing',
    href: '/collections/teams-certified',
    image: 'https://tr9ath-e6.myshopify.com/cdn/shop/files/jabra-evolve2-40-uc-stereo.jpg?v=1687355025&width=600',
    icon: IconMicrosoft,
    color: 'from-blue-500/20 to-blue-600/5',
  },
  {
    title: 'Wireless',
    description: 'Bluetooth & DECT freedom',
    href: '/collections/wireless',
    image: 'https://tr9ath-e6.myshopify.com/cdn/shop/collections/PRO-BT-DUO-Na-owijke-Duzy.jpg?v=1726063847&width=600',
    icon: IconBluetooth,
    color: 'from-brand-orange/20 to-brand-orange/5',
  },
  {
    title: 'Noise Cancelling',
    description: 'Focus without distractions',
    href: '/collections/active-noise-cancelling',
    image: 'https://tr9ath-e6.myshopify.com/cdn/shop/files/Poly_Voyager_4320_UC_Wireless_Stereo_Headset.avif?v=1728641063&width=600',
    icon: IconNoiseCancelling,
    color: 'from-purple-500/20 to-purple-600/5',
  },
  {
    title: 'Refurbished',
    description: 'Premium quality, great prices',
    href: '/collections/refurbished',
    image: 'https://tr9ath-e6.myshopify.com/cdn/shop/files/Jabra_Evolve_65_UC_Stereo_Headset.jpg?v=1728641034&width=600',
    icon: IconReturn,
    badge: 'Save 40%',
    color: 'from-emerald-500/20 to-emerald-600/5',
  },
];

export function FeaturedCollections() {
  return (
    <section className="py-16 lg:py-24 bg-surface-black">
      <div className="container-wide">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10"
        >
          <div>
            <span className="text-xs font-mono uppercase tracking-widest text-brand-orange mb-2 block">
              Categories
            </span>
            <h2 className="text-2xl md:text-3xl font-display font-bold text-accent-white">
              Shop by Use Case
            </h2>
          </div>
          <a href="/collections/all-products" className="text-sm text-accent-silver hover:text-brand-orange transition-colors flex items-center gap-1">
            View all categories
            <IconArrowRight size={14} />
          </a>
        </motion.div>

        {/* Collection Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {collections.map((collection, i) => (
            <motion.a
              key={collection.title}
              href={collection.href}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="group relative rounded-2xl overflow-hidden bg-surface-coal border border-surface-slate/30 hover:border-brand-orange/40 transition-all duration-300 hover:-translate-y-1"
            >
              {/* Gradient Background */}
              <div className={`absolute inset-0 bg-gradient-to-br ${collection.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />

              {/* Badge */}
              {collection.badge && (
                <div className="absolute top-3 right-3 z-20">
                  <span className="px-2 py-1 bg-emerald-500 text-white text-xs font-bold rounded-lg">
                    {collection.badge}
                  </span>
                </div>
              )}

              {/* Image Container */}
              <div className="relative aspect-[4/3] p-4">
                <div className="w-full h-full bg-white rounded-xl overflow-hidden">
                  <img
                    src={collection.image}
                    alt={collection.title}
                    className="w-full h-full object-contain p-4 transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
              </div>

              {/* Content */}
              <div className="relative p-4 pt-0">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-surface-graphite border border-surface-slate/30 flex items-center justify-center flex-shrink-0 group-hover:border-brand-orange/30 transition-colors">
                    <collection.icon size={20} className="text-brand-orange" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-accent-white group-hover:text-brand-orange transition-colors">
                      {collection.title}
                    </h3>
                    <p className="text-xs text-accent-silver mt-0.5">
                      {collection.description}
                    </p>
                  </div>
                  <IconArrowRight
                    size={18}
                    className="text-accent-silver opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 group-hover:text-brand-orange transition-all duration-300 flex-shrink-0"
                  />
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
